import React, { useState, useEffect, useMemo } from "react";
import { 
  Scissors, 
  AlertCircle, 
  FileText, 
  Calendar,
  Plus,
  X,
  Clipboard
} from "lucide-react";
import { useLocation } from "react-router-dom";
import useMedicalHistory from "../hooks/use-medical-history";
import HorizontalTimeline from "../components/medical-history/HorizontalTimeline";
import ConditionCard, { Condition } from "../components/medical-history/condition-card";
import HistoryFilters, { HistoryFilter } from "../components/medical-history/history-filters";
import DocumentViewer, { MedicalDocument } from "../components/medical-history/document-viewer";
import GlassCard from "@/components/ui/glass-card";
import PageContainer from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";

// Type definitions
type TabType = 'conditions' | 'allergies' | 'surgeries' | 'documents';

// Local interface to make it clear what we're handling in this component
interface ConditionViewModel extends Condition {}

interface DocumentViewModel extends MedicalDocument {}

/**
 * Ensures a status string is one of the valid condition statuses
 */
const normalizeStatus = (status: string): 'active' | 'chronic' | 'resolved' | 'remission' => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'active';
    case 'chronic':
      return 'chronic';
    case 'resolved':
      return 'resolved';
    case 'remission':
      return 'remission';
    default:
      // Default to 'active' if status is not recognized
      return 'active';
  }
};

/**
 * Ensures severity is one of the valid condition severities
 */
const normalizeSeverity = (severity: string): 'mild' | 'moderate' | 'severe' => {
  switch (severity.toLowerCase()) {
    case 'mild':
      return 'mild';
    case 'moderate':
      return 'moderate';
    case 'severe':
      return 'severe';
    default:
      return 'moderate';
  }
};

/**
 * Ensures treatment type is one of the valid treatment types
 */
const normalizeTreatmentType = (type: string): 'medication' | 'procedure' | 'therapy' | 'lifestyle' => {
  switch (type.toLowerCase()) {
    case 'medication':
      return 'medication';
    case 'procedure':
      return 'procedure';
    case 'therapy':
      return 'therapy';
    case 'lifestyle':
      return 'lifestyle';
    default:
      return 'medication';
  }
};

/**
 * Component to display detailed condition information
 */
const ConditionDetailView: React.FC<{
  condition: ConditionViewModel;
  onClose: () => void;
}> = ({ condition, onClose }) => {
  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-[var(--status-error-subtle)] text-[var(--status-error)]';
      case 'resolved':
        return 'bg-[var(--status-success-subtle)] text-[var(--status-success)]';
      case 'remission':
        return 'bg-[var(--status-warning-subtle)] text-[var(--status-warning)]';
      case 'chronic':
        return 'bg-[var(--clinical-heart-subtle)] text-[var(--clinical-heart)]';
      default:
        return 'bg-[var(--background-subtle)] text-[var(--text-secondary)]';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--background-surface)] rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-[var(--border-subtle)]">
          <div>
            <h2 className="text-xl font-bold text-[var(--text-primary)]">{condition.name}</h2>
            <div className="flex items-center mt-1 text-sm text-[var(--text-secondary)]">
              <span className="mr-3">Diagnosed: {formatDate(condition.diagnosisDate)}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(condition.status)}`}>
                {condition.status.charAt(0).toUpperCase() + condition.status.slice(1)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[var(--background-subtle)] text-[var(--text-secondary)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Description */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">About this Condition</h3>
            <p className="text-[var(--text-secondary)]">{condition.description}</p>
          </div>
          
          {/* Severity */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">Severity</h3>
            <div className="px-3 py-1.5 bg-[var(--background-subtle)] inline-block rounded-md text-[var(--text-secondary)]">
              <span className="capitalize">{condition.severity}</span>
            </div>
          </div>
          
          {/* Symptoms */}
          {condition.symptoms && condition.symptoms.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">Symptoms</h3>
              <div className="flex flex-wrap gap-2">
                {condition.symptoms.map((symptom, index) => (
                  <span 
                    key={index} 
                    className="bg-[var(--background-subtle)] text-[var(--text-secondary)] px-3 py-1.5 rounded-md"
                  >
                    {symptom}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Treatments */}
          {condition.treatments && condition.treatments.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">Treatments</h3>
              <div className="space-y-4">
                {condition.treatments.map((treatment) => (
                  <div 
                    key={treatment.id} 
                    className="p-3 bg-[var(--background-subtle)] rounded-lg"
                  >
                    <div className="flex items-start">
                      <div className="flex-1">
                        <h4 className="text-[var(--text-primary)] font-medium">{treatment.name}</h4>
                        <div className="text-sm text-[var(--text-secondary)] mt-1">
                          <p>Type: <span className="capitalize">{treatment.type}</span></p>
                          {treatment.dosage && <p className="mt-1">Dosage: {treatment.dosage}</p>}
                          {treatment.frequency && <p className="mt-1">Frequency: {treatment.frequency}</p>}
                          <p className="mt-1">Started: {formatDate(treatment.startDate)}</p>
                          {treatment.endDate && <p className="mt-1">Ended: {formatDate(treatment.endDate)}</p>}
                        </div>
                        {treatment.notes && (
                          <div className="mt-2 text-sm">
                            <p className="font-medium text-[var(--text-primary)]">Notes</p>
                            <p className="text-[var(--text-secondary)]">{treatment.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Diagnostics (if available) */}
          {condition.diagnostics && condition.diagnostics.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">Diagnostic Tests</h3>
              <div className="space-y-3">
                {condition.diagnostics.map((diagnostic) => (
                  <div 
                    key={diagnostic.id} 
                    className="p-3 bg-[var(--background-subtle)] rounded-lg"
                  >
                    <h4 className="text-[var(--text-primary)] font-medium">{diagnostic.name}</h4>
                    <div className="text-sm text-[var(--text-secondary)] mt-1">
                      <p>Date: {formatDate(diagnostic.date)}</p>
                      <p className="mt-1">Result: {diagnostic.result}</p>
                    </div>
                    {diagnostic.attachmentUrl && (
                      <a 
                        href={diagnostic.attachmentUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block mt-2 text-sm text-[var(--brand-primary)] hover:underline"
                      >
                        View attachment
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-[var(--border-subtle)] p-4 bg-[var(--background-subtle)]">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[var(--background-surface)] border border-[var(--border-subtle)] rounded-md hover:bg-[var(--background-interactive)] text-[var(--text-primary)]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MedicalHistory: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('conditions');
  const [showConditionDetail, setShowConditionDetail] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState<ConditionViewModel | null>(null);
  const [showDocumentDetail, setShowDocumentDetail] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentViewModel | null>(null);
  
  const [, setFilters] = useState<HistoryFilter>({
    timeRange: 'all-time',
    categories: [],
    providers: [],
    severity: [],
    searchTerm: '',
  });
  
  // Get medical history data from hook
  const { 
    conditions, 
    allergies, 
    surgeries,
    documents,
    timelineEvents,
    providers,
    categories,
    isLoading, 
    error 
  } = useMedicalHistory();
  
  // Set initial tab based on route query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab && ['conditions', 'allergies', 'surgeries', 'documents'].includes(tab)) {
      setActiveTab(tab as TabType);
    }
  }, [location.search]);
  
  // Handle condition selection
  const handleConditionSelect = (condition: ConditionViewModel): void => {
    setSelectedCondition(condition);
    setShowConditionDetail(true);
  };
  
  // Handle condition modal close
  const handleConditionClose = (): void => {
    setShowConditionDetail(false);
    setSelectedCondition(null);
  };
  
  // Handle document selection
  const handleDocumentSelect = (document: DocumentViewModel): void => {
    setSelectedDocument(document);
    setShowDocumentDetail(true);
  };
  
  // Handle document modal close
  const handleDocumentClose = (): void => {
    setShowDocumentDetail(false);
    setSelectedDocument(null);
  };
  
  // Handle filter changes
  const handleFilterChange = (newFilters: HistoryFilter): void => {
    setFilters(newFilters);
  };

  // Handle timeline event click
  const handleTimelineEventClick = (event: any): void => {
    // Determine the type of event and open the appropriate modal
    if (event.category?.toLowerCase() === 'condition') {
      const matchingCondition = conditions.find(c => c.id === event.id);
      if (matchingCondition) {
        handleConditionSelect(matchingCondition);
      }
    } else if (event.category?.toLowerCase() === 'document') {
      const matchingDocument = documents.find(d => d.id === event.id);
      if (matchingDocument) {
        handleDocumentSelect(matchingDocument);
      }
    }
  };
  
  // Render tab content based on active tab
  const renderTabContent = (): JSX.Element => {
    switch (activeTab) {
      case 'conditions':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {conditions.map((condition) => (
              <ConditionCard 
                key={condition.id} 
                condition={condition} 
                onSelect={handleConditionSelect}
              />
            ))}
            {conditions.length === 0 && (
              <div className="col-span-full text-center p-8 text-[var(--text-secondary)]">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No conditions found</p>
                <p className="mt-2">Try adjusting your filters or search terms</p>
              </div>
            )}
          </div>
        );
      case 'allergies':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {allergies.map((allergy) => (
              <GlassCard 
                key={allergy.id} 
                padding="md"
                variant="default"
                className="hover:shadow-lg border hover:border-[var(--brand-primary)]/30 transition-all duration-300 cursor-pointer group"
                onClick={() => {
                  // Normalize status and severity to match expected types
                  const normalizedAllergy = {
                    ...allergy,
                    status: normalizeStatus(allergy.status),
                    severity: normalizeSeverity(allergy.severity),
                    treatments: allergy.treatments?.map(treatment => ({
                      ...treatment,
                      type: normalizeTreatmentType(treatment.type)
                    })) || []
                  };
                  handleConditionSelect(normalizedAllergy);
                }}
              >
                <h3 className="font-medium mb-2 text-[var(--text-primary)]">{allergy.name}</h3>
                <div className="text-sm text-[var(--text-secondary)] mb-1">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-[var(--status-warning-subtle)] text-[var(--status-warning)] text-xs font-medium mr-2">
                    {allergy.severity}
                  </span>
                  <span>Since {new Date(allergy.diagnosisDate).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mt-2">{allergy.description}</p>
              </GlassCard>
            ))}
            {allergies.length === 0 && (
              <div className="col-span-full text-center p-8 text-[var(--text-secondary)]">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No allergies found</p>
                <p className="mt-2">Try adjusting your filters or search terms</p>
              </div>
            )}
          </div>
        );
      case 'surgeries':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {surgeries.map((surgery) => (
              <GlassCard 
                key={surgery.id} 
                padding="md"
                variant="default"
                className="hover:shadow-lg border hover:border-[var(--brand-primary)]/30 transition-all duration-300 cursor-pointer group"
                onClick={() => {
                  // Normalize status and severity to match expected types
                  const normalizedSurgery = {
                    ...surgery,
                    status: normalizeStatus(surgery.status),
                    severity: normalizeSeverity(surgery.severity),
                    treatments: surgery.treatments?.map(treatment => ({
                      ...treatment,
                      type: normalizeTreatmentType(treatment.type)
                    })) || []
                  };
                  handleConditionSelect(normalizedSurgery);
                }}
              >
                <h3 className="font-medium mb-2 text-[var(--text-primary)]">{surgery.name}</h3>
                <div className="flex items-center text-sm text-[var(--text-secondary)] mb-2">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>{new Date(surgery.diagnosisDate).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-[var(--text-secondary)] line-clamp-2">{surgery.description}</p>
              </GlassCard>
            ))}
            {surgeries.length === 0 && (
              <div className="col-span-full text-center p-8 text-[var(--text-secondary)]">
                <Scissors className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No surgeries found</p>
                <p className="mt-2">Try adjusting your filters or search terms</p>
              </div>
            )}
          </div>
        );
      case 'documents':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {documents.map((document) => (
              <GlassCard 
                key={document.id} 
                padding="md"
                variant="default"
                className="hover:shadow-lg border hover:border-[var(--brand-primary)]/30 transition-all duration-300 cursor-pointer group"
                onClick={() => handleDocumentSelect(document)}
              >
                <div className="flex items-start">
                  <div className="p-2 bg-[var(--brand-primary-subtle)] rounded-md mr-3">
                    <FileText className="w-5 h-5 text-[var(--brand-primary)]" />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <h3 className="font-medium mb-1 text-[var(--text-primary)] truncate">{document.title}</h3>
                    <div className="flex items-center text-xs text-[var(--text-secondary)] mb-2">
                      <span>{new Date(document.date).toLocaleDateString()}</span>
                      <span className="mx-1">•</span>
                      <span>{document.type}</span>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] line-clamp-2">{document.description}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
            {documents.length === 0 && (
              <div className="col-span-full text-center p-8 text-[var(--text-secondary)]">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No documents found</p>
                <p className="mt-2">Try adjusting your filters or search terms</p>
              </div>
            )}
          </div>
        );
      default:
        return <div>Select a tab to view medical history</div>;
    }
  };
  
  // Organize data for export or summary
  const medicalSummary = useMemo(() => {
    return {
      conditions: conditions?.length || 0,
      allergies: allergies?.length || 0,
      surgeries: surgeries?.length || 0,
      documents: documents?.length || 0,
      totalRecords: (conditions?.length || 0) + (allergies?.length || 0) + (surgeries?.length || 0) + (documents?.length || 0)
    };
  }, [conditions, allergies, surgeries, documents]);
  
  return (
    <PageContainer
      title="Medical History"
      description={`${medicalSummary.totalRecords} items in your medical history`}
      icon={<Clipboard className="h-5 w-5" />}
      actions={
        <Button 
          variant="default" 
          size="sm" 
          className="flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Add Health Record
        </Button>
      }
    >
      {/* Horizontal Timeline (New Component) */}
      <GlassCard variant="default" padding="md" className="mb-6 shadow-sm">
        <h2 className="text-xl font-semibold mb-4 text-[var(--text-primary)]">Health Timeline</h2>
        <HorizontalTimeline 
          events={timelineEvents || []} 
          onEventClick={handleTimelineEventClick}
          className="mt-2" 
        />
      </GlassCard>
      
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left column - main content */}
        <div className="flex-1">
          {/* Tabs */}
          <div className="mb-6 overflow-x-auto pb-2">
            <div className="flex w-max space-x-1 bg-slate-100/50 dark:bg-slate-800/50 p-1.5 rounded-2xl border border-white/20">
              <button
                className={`py-2 px-5 rounded-xl font-medium text-sm transition-all duration-200 ${
                  activeTab === 'conditions'
                    ? 'bg-white dark:bg-slate-700 shadow-sm text-[var(--brand-primary)] dark:text-white pointer-events-none'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                onClick={() => setActiveTab('conditions')}
              >
                Conditions
              </button>
              <button
                className={`py-2 px-5 rounded-xl font-medium text-sm transition-all duration-200 ${
                  activeTab === 'allergies'
                    ? 'bg-white dark:bg-slate-700 shadow-sm text-[var(--brand-primary)] dark:text-white pointer-events-none'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                onClick={() => setActiveTab('allergies')}
              >
                Allergies
              </button>
              <button
                className={`py-2 px-5 rounded-xl font-medium text-sm transition-all duration-200 ${
                  activeTab === 'surgeries'
                    ? 'bg-white dark:bg-slate-700 shadow-sm text-[var(--brand-primary)] dark:text-white pointer-events-none'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                onClick={() => setActiveTab('surgeries')}
              >
                Surgeries
              </button>
              <button
                className={`py-2 px-5 rounded-xl font-medium text-sm transition-all duration-200 ${
                  activeTab === 'documents'
                    ? 'bg-white dark:bg-slate-700 shadow-sm text-[var(--brand-primary)] dark:text-white pointer-events-none'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                onClick={() => setActiveTab('documents')}
              >
                Documents
              </button>
            </div>
          </div>
          
          {/* Main content */}
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--brand-primary)]"></div>
            </div>
          ) : error ? (
            <div className="mt-6 p-4 bg-[var(--status-error-subtle)] text-[var(--status-error)] rounded-md">
              <p>Error loading medical history. Please try again later.</p>
            </div>
          ) : (
            renderTabContent()
          )}
        </div>
        
        {/* Right column - filters */}
        <div className="lg:w-72 sticky top-4 self-start">
          <HistoryFilters 
            availableCategories={categories || []} 
            availableProviders={providers || []} 
            onFilterChange={handleFilterChange} 
          />
        </div>
      </div>
      
      {/* Condition detail modal */}
      {showConditionDetail && selectedCondition && (
        <ConditionDetailView 
          condition={selectedCondition} 
          onClose={handleConditionClose} 
        />
      )}
      
      {/* Document viewer modal */}
      {showDocumentDetail && selectedDocument && (
        <DocumentViewer 
          document={selectedDocument} 
          onClose={handleDocumentClose} 
        />
      )}
    </PageContainer>
  );
};

export default MedicalHistory; 